import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft, ArrowRight, Lock, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const AdminLogin = () => {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("admin.invalidCreds");
      setError(msg);
      setPassword("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="admin-login-page">
      {/* Decorative grid bg */}
      <div className="fixed inset-0 -z-10 [background-image:linear-gradient(to_right,rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.04)_1px,transparent_1px)] [background-size:32px_32px]" />

      <header className="px-6 py-4 flex items-center justify-between bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.png" alt="RTC logo" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-bold text-slate-900 text-sm leading-tight">RTC Admin</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Ragime Trading &amp; Contracting</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="admin-login-back-site">
            <ArrowLeft size={14} /> {t("admin.backToSite")}
          </Link>
          <LanguageSwitcher variant="minimal" />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-900/5 p-8 admin-fade-up" data-testid="admin-login-card">
            <div className="mb-7 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-50 text-orange-600 rounded-xl mb-4">
                <Lock size={20} />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">{t("admin.loginTitle")}</h1>
              <p className="mt-1 text-sm text-slate-500">{t("admin.loginSubtitle")}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">{t("admin.email")}</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="admin-input pl-9"
                    autoComplete="email"
                    placeholder="admin@rtc-qatar.com"
                    data-testid="admin-login-email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">{t("admin.password")}</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    required
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="admin-input pl-9"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    data-testid="admin-login-password"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm" data-testid="admin-login-error">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
                </div>
              )}

              <button type="submit" disabled={submitting} className="admin-btn-primary w-full py-3" data-testid="admin-login-submit">
                {submitting ? (
                  <><Loader2 size={16} className="animate-spin" /> {t("admin.signingIn")}</>
                ) : (
                  <>{t("admin.signIn")} <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Authorized personnel only · RTC © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
